import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Heading,
  HStack,
  Link,
  Spinner,
  Stack,
  Tag,
  Text,
  useColorModeValue,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Code,
  Alert,
  AlertIcon,
  useToast
} from '@chakra-ui/react';
import { ChevronLeftIcon, ExternalLinkIcon, DownloadIcon } from '@chakra-ui/icons';
import { useQuery } from 'react-query';
import ReactMarkdown from 'react-markdown';

import { getPackageDetails } from '../services/packageService';

export default function PackageDetailPage() {
  const { packageId } = useParams<{ packageId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  
  // Move color mode values to top level of component to follow React Hooks rules
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const markdownBgColor = useColorModeValue('gray.50', 'gray.700');
  const codeBgColor = useColorModeValue('gray.100', 'gray.800');
  
  const { data: pkg, isLoading, isError, error } = useQuery(
    ['package', packageId],
    () => packageId ? getPackageDetails(packageId) : Promise.reject('No package ID provided'),
    {
      retry: 1,
      onError: (err) => {
        console.error('Error fetching package details:', err);
      }
    }
  );

  const handleInstall = () => {
    if (!pkg) return;
    
    // In a real implementation, this might call an API endpoint or show installation instructions
    toast({
      title: 'Installation Command',
      description: `Run: mcppm install ${pkg.name}`,
      status: 'info',
      duration: 9000,
      isClosable: true,
      position: 'top',
    });
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" height="50vh">
        <Spinner size="xl" color="brand.500" thickness="4px" />
      </Flex>
    );
  }

  if (isError || !pkg) {
    return (
      <Container maxW="container.lg" py={8}>
        <Button 
          leftIcon={<ChevronLeftIcon />} 
          onClick={() => navigate('/packages')} 
          mb={4} 
          variant="ghost"
        >
          Back to Packages
        </Button>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Text>Failed to load package details. {(error as Error)?.message || 'Please try again later.'}</Text>
        </Alert>
      </Container>
    );
  }

  // Format dates for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <Container maxW="container.lg" py={8}>
      <Button 
        leftIcon={<ChevronLeftIcon />} 
        onClick={() => navigate('/packages')} 
        mb={4} 
        variant="ghost"
      >
        Back to Packages
      </Button>
      
      <Box bg={bgColor} p={6} borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
        <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'flex-start', md: 'center' }} mb={6} wrap="wrap" gap={4}>
          <Box>
            <Heading size="lg" color="brand.500">{pkg.name}</Heading>
            <Text mt={2} fontSize="lg">{pkg.description}</Text>
          </Box>
          
          <HStack spacing={4}>
            <Button
              leftIcon={<DownloadIcon />}
              colorScheme="brand"
              size="md"
              onClick={handleInstall}
            >
              Install
            </Button>
            
            <Link href={pkg.github_url} isExternal>
              <Button rightIcon={<ExternalLinkIcon />} variant="outline" size="md">
                GitHub
              </Button>
            </Link>
          </HStack>
        </Flex>

        <Divider mb={6} />
        
        <Stack direction={{ base: 'column', md: 'row' }} spacing={8}>
          {/* Package stats */}
          <Box minW={{ md: '200px' }}>
            <VStack align="stretch" spacing={4}>
              <Stat>
                <StatLabel>Version</StatLabel>
                <StatNumber>{pkg.latest_version}</StatNumber>
                <StatHelpText>{pkg.versions.length} version(s) available</StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel>Downloads</StatLabel>
                <StatNumber>{pkg.downloads.toLocaleString()}</StatNumber>
              </Stat>
              
              <Stat>
                <StatLabel>License</StatLabel>
                <StatNumber>{pkg.license}</StatNumber>
              </Stat>
              
              <Stat>
                <StatLabel>Author</StatLabel>
                <StatNumber>{pkg.author}</StatNumber>
              </Stat>
              
              <Stat>
                <StatLabel>Published</StatLabel>
                <StatNumber>{formatDate(pkg.created_at)}</StatNumber>
                <StatHelpText>Updated {formatDate(pkg.updated_at)}</StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel>Root Directory</StatLabel>
                <StatNumber>{pkg.root_dir || "/"}</StatNumber>
                <StatHelpText>Source location in repo</StatHelpText>
              </Stat>
            </VStack>
          </Box>
          
          {/* Package content tabs */}
          <Box flex="1">
            <Tabs colorScheme="brand" isLazy>
              <TabList>
                <Tab>Readme</Tab>
                <Tab>Versions</Tab>
                <Tab>Installation</Tab>
              </TabList>
              
              <TabPanels>
                {/* README tab */}
                <TabPanel>
                <Box 
                    className="markdown-body" 
                    p={4} 
                    borderRadius="md" 
                    bg={markdownBgColor}
                    sx={{
                      'h1, h2, h3, h4, h5, h6': {
                        marginTop: '24px',
                        marginBottom: '16px',
                        fontWeight: 'bold',
                        lineHeight: '1.25'
                      },
                      'h1': { fontSize: '2em', borderBottom: '1px solid', borderColor: borderColor, pb: '0.3em' },
                      'h2': { fontSize: '1.5em', borderBottom: '1px solid', borderColor: borderColor, pb: '0.3em' },
                      'p': { marginBottom: '16px' },
                      'pre': { bg: codeBgColor, p: 3, borderRadius: 'md', overflowX: 'auto' },
                      'code': { fontFamily: 'monospace', bg: codeBgColor, px: 1, borderRadius: 'sm' },
                      'a': { color: 'brand.500' },
                      'ul, ol': { paddingLeft: '2em', marginBottom: '16px' },
                      'li': { marginBottom: '0.25em' }
                    }}
                  >
                    <ReactMarkdown>
                      {pkg.readme}
                    </ReactMarkdown>
                  </Box>
                </TabPanel>
                
                {/* Versions tab */}
                <TabPanel>
                  <Stack spacing={4}>
                    {pkg.versions.map(version => (
                      <Box 
                        key={version.version} 
                        p={4} 
                        borderWidth="1px" 
                        borderRadius="md" 
                        borderColor={borderColor}
                      >
                        <Flex justify="space-between" align="center">
                          <Tag size="lg" colorScheme="blue" variant="solid">v{version.version}</Tag>
                          <Text fontSize="sm">{formatDate(version.published_at)}</Text>
                        </Flex>
                      </Box>
                    ))}
                  </Stack>
                </TabPanel>
                
                {/* Installation tab */}
                <TabPanel>
                  <VStack align="stretch" spacing={6}>
                    <Box>
                      <Heading size="md" mb={2}>Command Line</Heading>
                      <Code p={4} borderRadius="md" fontSize="md" display="block">
                        mcppm install {pkg.name}
                      </Code>
                    </Box>
                    
                    <Box>
                      <Heading size="md" mb={2}>Configuration</Heading>
                      <Text mb={2}>After installation, you can configure the server:</Text>
                      <Code p={4} borderRadius="md" fontSize="md" display="block">
                        mcppm config {pkg.name} VARIABLE_NAME value
                      </Code>
                    </Box>
                    
                    <Box>
                      <Heading size="md" mb={2}>Starting the Server</Heading>
                      <Text mb={2}>Start the MCP server:</Text>
                      <Code p={4} borderRadius="md" fontSize="md" display="block">
                        mcppm start {pkg.name}
                      </Code>
                    </Box>
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </Stack>
      </Box>
    </Container>
  );
}
